import {
  Chip,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";

import { OverflowTooltip } from "@/components/DesignSystem";
import type { IpEvent, FailureBreakdown } from "@/types";
import { colors, riskColors, getRiskLevel } from "@/theme/eventsTokens.ts";
import ActivitySparkline from "./ActivitySparkline.tsx";

const cellSx = {
  borderColor: colors.borderLight,
  padding: { xs: "8px 6px", md: "10px 12px", lg: "12px 16px" },
  overflow: "hidden",
} as const;



function formatTimestamp(epoch: number): string {
  if (!epoch) return "N/A";
  const d = new Date(epoch * 1000);
  return (
    d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
}

export default function EventRow({ event }: { readonly event: IpEvent }) {
  const score = event.fraud_score;
  const level = getRiskLevel(score);

  return (
    <TableRow
      sx={{
        "&:hover": { backgroundColor: colors.backgroundSecondary },
        "& td": { borderColor: colors.borderLight },
      }}
    >
      <IpCell ip={event.ip_address} />
      <ScoreCell score={score} level={level} />
      <TextCell>{event.country_code || "N/A"}</TextCell>
      <AsnCell metadata={event.metadata} />
      <TableCell sx={cellSx}>
        <Tooltip title={event.last_active_date ? `Last seen: ${formatDate(event.last_active_date)}` : ""}>
          <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary, whiteSpace: "nowrap" }}>
            {formatTimestamp(event.last_activity)}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={cellSx}>
        {event.daily_activity && event.daily_activity.length > 0 && (
          <ActivitySparkline points={event.daily_activity} />
        )}
      </TableCell>
      <CorroboratedCell reported={event.reported_by_others} />
      <NumericCell value={event.blocked_recent} />
      <FailuresCell value={event.failures_recent} breakdown={event.failure_breakdown} />
      <NumericCell value={event.successful_recent} pr={{ xs: "12px", md: "20px", lg: "24px" }} />

    </TableRow>
  );
}

function IpCell({ ip }: { readonly ip: string }) {
  return (
    <TableCell sx={cellSx}>
      <OverflowTooltip sx={{ fontFamily: "monospace", fontWeight: 500, fontSize: "0.875rem", color: colors.textPrimary }}>
        {ip}
      </OverflowTooltip>
    </TableCell>
  );
}

function ScoreCell({ score, level }: { readonly score: number; readonly level: string }) {
  return (
    <TableCell sx={cellSx}>
      <Chip
        label={score}
        size="small"
        sx={{ backgroundColor: riskColors[level as keyof typeof riskColors].main, color: colors.primaryForeground, fontWeight: 700, fontSize: "0.8125rem", minWidth: "48px", height: "26px" }}
      />
    </TableCell>
  );
}

function TextCell({ children }: { readonly children: string }) {
  return (
    <TableCell sx={cellSx}>
      <OverflowTooltip sx={{ fontSize: "0.875rem", color: colors.textSecondary }}>
        {children}
      </OverflowTooltip>
    </TableCell>
  );
}

function AsnCell({ metadata }: { readonly metadata: IpEvent["metadata"] }) {
  return (
    <TableCell sx={cellSx}>
      <OverflowTooltip sx={{ fontSize: "0.8125rem", color: colors.textSecondary }}>
        {metadata.asn ? `${metadata.asn} (${metadata.as_name})` : "N/A"}
      </OverflowTooltip>
    </TableCell>
  );
}

function CorroboratedCell({ reported }: { readonly reported: boolean }) {
  return (
    <TableCell sx={{ ...cellSx, textAlign: "center" }}>
      {reported && (
        <Tooltip title="Reported by others">
          <ShieldIcon sx={{ fontSize: 18, color: riskColors.low.main }} />
        </Tooltip>
      )}
    </TableCell>
  );
}

function FailuresCell({ value, breakdown }: { readonly value?: number | null; readonly breakdown?: FailureBreakdown | null }) {
  const count = value ?? 0;
  if (count > 0 && breakdown) {
    return (
      <TableCell sx={{ ...cellSx, textAlign: "right" }}>
        <Tooltip
          title={
            <>
              <div>User not found: {breakdown.user_not_found}</div>
              <div>Invalid password: {breakdown.invalid_password}</div>
              <div>Second factor: {breakdown.failed_second_factor}</div>
            </>
          }
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              color: colors.textSecondary,
              fontVariantNumeric: "tabular-nums",
              cursor: "help",
              textDecoration: "underline dotted",
              textUnderlineOffset: "3px",
            }}
          >
            {count}
          </Typography>
        </Tooltip>
      </TableCell>
    );
  }
  return <NumericCell value={value} />;
}

function NumericCell({ value, pr }: { readonly value?: number | null; readonly pr?: Record<string, string> }) {
  return (
    <TableCell sx={{ ...cellSx, textAlign: "right", ...(pr && { pr }) }}>
      <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary, fontVariantNumeric: "tabular-nums" }}>
        {value ?? 0}
      </Typography>
    </TableCell>
  );
}
